/* eslint-disable @typescript-eslint/no-unused-vars */
"use client";
import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  useGlobalFilter,
  usePagination,
  useRowSelect,
  useSortBy,
  useTable,
} from "react-table";
import Screen from "../components/Screen";
import ProfileHeaderSection from "../components/ProfileHeaderSection";
import { Button } from "../components/forms/DashboardButton";
import { BsPlusSquareDotted } from "react-icons/bs";
import ContactCard from "../components/Box/ContactCard";
import TableLayout from "../components/Table/TableLayout";
import TablePagination from "../components/Table/TablePagination";
import TableHeader from "../components/Table/TableColumn";
import DataTableCard from "../components/Table/DataTableCard";
import Modal from "../components/modal/AddEmployer";
import { box, list } from "../components/image";
import TableSearch from "../components/Table/TableSearch";
import Actions from "../components/Table/TableAction";
import { getUser } from "../services/api";
import { useGlobalContext } from "../context/globalContext";
import TransitionsModal from "../components/modal/CustomModal";
import {
  useDeleteEmployee,
  useEditEmployee,
  useGetEmployees,
  useRegisterEmployee,
} from "../hooks/useEmployee";
import EditEmployee from "./Hr/EditEmployee";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { ArrowUpDown, Phone } from "lucide-react";
import dayjs from "dayjs";
import DataTable from "../components/Table/data-table";
import CustomPagination from "../components/Table/pagination/Pagination";
import { useLocation, useSearchParams } from "react-router-dom";
const Directory = () => {
  const { user, token } = useGlobalContext();
  const { employee, isPending: isLoading } = useGetEmployees();

  const { mutateDeleteEmployee, isPending } = useDeleteEmployee();

  const { mutateEditEmployee, isPending: isEditing } = useEditEmployee();
  const { mutateRegisterEmployee, isPending: isRegistering } =
    useRegisterEmployee();

  const [image, setImage] = useState<File | null>(null);
  const [deleteEmployee, setDeleteEmployee] = useState<string | null>(null);
  const [filters, setFilters] = useState<string>("");
  const [showModal, setShowModal] = useState(false);
  const [showList, setShowList] = useState(true);
  const [showBox, setShowBox] = useState(false);
  const [edit, setEdit] = useState<{
    id: string;
    name: string;
    lastname: string;
    email: string;
    department: string;
    job_role: string;
    datejoined: string;
    city: string;
    country: string;
    employment_type: string;
    phone: string;
    lead: string;
  } | null>(null);
  const [activate, setActivate] = useState<{
    id: string;
    status: string;
  } | null>(null);

  const userdept = user?.department;
  const dept = userdept;

  const schema = z.object({
    id: z.string(),
    firstname: z.string().min(3, {
      message: "Please enter a first name",
    }),
    lastname: z.string().min(3, {
      message: "Please enter a last name",
    }),
    email: z.string().email().min(3, {
      message: "Please enter a valid email",
    }),

    department: z.string().min(3, {
      message: "Please enter a department",
    }),

    job_role: z.string().min(3, {
      message: "Please enter a job role",
    }),

    datejoined: z.date({
      message: "Please enter a date joined",
    }),

    city: z.string().min(3, {
      message: "Please enter a city",
    }),

    country: z.string().min(3, {
      message: "Please enter a country",
    }),

    phone: z.string().min(10, {
      message: "Please enter a phone",
    }),

    employment_type: z.string().min(3, {
      message: "Please enter employment type",
    }),
    lead: z.string().min(3, {
      message: "Please enter Lead",
    }),
  });

  const form = useForm({
    resolver: zodResolver(schema),
    mode: "onChange",
    defaultValues: {
      id: (edit?.id as string) || "1",
      firstname: "",
      lastname: "",
      email: "",
      department: "People Operations",
      job_role: "",
      datejoined: new Date(),
      city: "",
      country: "Nigeria",
      employment_type: "Contract",
      phone: "",
      lead: "",
    },
  });

  const { setValue, reset } = form;

  const handleAdd = () => {
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    reset();
  };

  const handleBox = () => {
    setShowList(false);
    setShowBox(true);
  };

  const handlelist = () => {
    setShowBox(false);
    setShowList(true);
    setCurrentPage(0);
  };

  const handleDelete = (id: string) => {
    setDeleteEmployee(id);
  };

  const confirmDelete = () => {
    mutateDeleteEmployee(deleteEmployee, {
      onSuccess: (data) => {
        setDeleteEmployee(null);
      },
    });
  };

  const handleActive = (item: { id: string; status: string }) => {
    setActivate(item);
  };

  const confirmActivation = () => {
    const payload = {
      id: activate?.id,
      status: activate?.status === "active" ? "inactive" : "active",
    };
    mutateEditEmployee(payload, {
      onSuccess: () => {
        setActivate(null);
      },
      onError: (error) => {
        console.log(error);
      },
    });
  };

  const handleCloseActive = () => {
    setActivate(null);
  };

  const handleEdit = (item: any) => {
    setEdit(item);
    setValue("id", item.id);
    setValue("firstname", item.name);
    setValue("lastname", item.lastname);
    setValue("email", item.email);
    setValue("department", item.department);
    setValue("job_role", item.job_role);
    setValue("datejoined", item.datejoined ?? item.created);
    setValue("city", item.city);
    setValue("country", item.country);
    setValue("employment_type", item.employment_type);
    setValue("phone", item.phone);
    setValue("lead", item.lead);
  };

  const onSubmit = async (data: z.infer<typeof schema>) => {
    const date = dayjs(new Date(data.datejoined)).format("YYYY/MM/DD");
    if (edit) {
      mutateEditEmployee(data, {
        onSuccess: (data) => {
          setEdit(null);
          reset();
        },
        onError: (error) => {
          console.log(error);
        },
      });
      return;
    }
    const formData = new FormData();
    if (image) {
      formData.append("image", image);
    }
    formData.append("firstname", data.firstname);
    formData.append("lastname", data.lastname);
    formData.append("email", data.email);
    formData.append("department", data.department);
    formData.append("job_role", data.job_role);
    formData.append("datejoined", date);
    formData.append("city", data.city);
    formData.append("country", data.country);
    formData.append("phone", data.phone);
    formData.append("employment_type", data.employment_type);
    formData.append("lead", data.lead);

    mutateRegisterEmployee(formData, {
      onSuccess: (data) => {
        closeModal();
      },
    });
  };

  const handleCloseEdit = () => {
    setEdit(null);
    reset();
  };

  const transformedEmployees = Array.isArray(employee)
    ? employee.map(
        (event: {
          id: string;
          firstname: string;
          lastname: string;
          email: string;
          department: string;
          job_role: string;
          created: string;
          datejoined: string;
          city: string;
          country: string;
          employment_type: string;
          phone: string;
          status: string;
          image: string;
          lead: string;
        }) => {
          const {
            id,
            firstname,
            lastname,
            email,
            department,
            phone,
            city,
            job_role,
            created,
            datejoined,
            country,
            status,
            image,
            employment_type,
            lead,
          } = event;

          return {
            id: id,
            name: firstname || "N/A",
            lastname: lastname || "N/A",
            image: image || "N/A",
            email: email || "N/A",
            phone: phone || "N/A",
            city: city || "N/A",
            job_role: job_role || "N/A",
            department: department || "N/A",
            datejoined: datejoined || created,
            country: country || "N/A",
            status: status || "N/A",
            employment_type: employment_type || "N/A",
            lead: lead || "N/A",
          };
        }
      )
    : [];

  const column = useMemo(
    () => [
      {
        accessorKey: "name",
        header: ({ column }: { column: any }) => {
          return (
            <button
              className="flex group"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              Name
              <ArrowUpDown className="ml-2 h-4 w-4 hidden group-hover:block" />
            </button>
          );
        },
        cell: ({ row }: { row: any }) => {
          return (
            <div className="text-lightDark flex items-center gap-1 text-[12px]">
              <div>
                <img
                  src={row.original.image}
                  className="w-[30px] h-[30px] rounded-lg"
                  alt="profile"
                />
              </div>
              <div>
                <p>
                  {row.original.name} {row.original.lastname}
                </p>
                <p className="text-[#6B788E]">{row.original.email}</p>
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: "phone",
        header: "Phone Number",
        cell: ({ row }: { row: any }) => {
          return (
            <>
              <p className="text-[12px]">{row.original.phone}</p>
            </>
          );
        },
      },
      {
        accessorKey: "city",
        header: "City",
        cell: ({ row }: { row: any }) => {
          return (
            <>
              <p className=" text-[12px]">{row.original.city}</p>
            </>
          );
        },
      },
      {
        accessorKey: "job_role",
        header: "Role",
        cell: ({ row }: { row: any }) => {
          return (
            <>
              <p className=" text-[12px]">{row.original.job_role}</p>
            </>
          );
        },
      },
      {
        accessorKey: "department",
        header: "Department",
        cell: ({ row }: { row: any }) => {
          return (
            <>
              <p className=" text-[12px]">{row.original.department}</p>
            </>
          );
        },
      },
      {
        accessorKey: "date",
        header: "Date Joined",
        cell: ({ row }: { row: any }) => {
          return (
            <>
              <p className=" text-[12px]">{row.original.datejoined}</p>
            </>
          );
        },
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }: { row: any }) => {
          return (
            <>
              <p
                className={
                  row.original.status === "active"
                    ? "text-[#027A48] w-full text-[12px] capitalize bg-[#ECFDF3] p-1 rounded"
                    : "text-yellow-500 w-full  capitalize text-[12px] bg-yellow-100 p-1 rounded"
                }
              >
                {row.original.status}
              </p>
            </>
          );
        },
      },
      {
        accessorKey: "",
        header: " ",
        cell: ({ row }: { row: any }) => {
          if (dept === "People Operations") {
            return (
              <Actions
                handleClick={() => handleEdit(row.original)}
                handleDelete={() => handleDelete(row.original.id)}
                handleActive={() => handleActive(row.original)}
                active={row.original.status}
              />
            );
          }
        },
      },
    ],
    []
  );

  const itemsPerPage = 10;

  const initialPage = parseInt(localStorage.getItem("currentPage") || "0", 10);
  const [currentPage, setCurrentPage] = useState(initialPage);

  // useEffect(() => {
  //   localStorage.setItem("currentPage", currentPage.toString());
  // }, [currentPage]);

  const paginatedEmployees = employee?.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage
  );

  return (
    <div className="bg-white w-full">
      {/* Delete */}
      <TransitionsModal
        open={!!deleteEmployee}
        setOpen={() => setDeleteEmployee("")}
        title={"Delete Employee?"}
        handleCloseModal={() => setDeleteEmployee(null)}
      >
        <div className="flex flex-col items-center w-full">
          <p className="text-center text-lightDark">
            Are you sure you want to delete this employee?
          </p>
          <p>This action is Irreversible!</p>
        </div>
        <div className="flex-row gap-4 flex justify-between mx-auto mt-8">
          <button
            className={`bg-yellow-500 w-$full rounded text-center py-3 px-6 cursor-pointer transition text-lightDark duration-300 ease-in-out hover:text-white hover:bg-opacity-80`}
            onClick={() => setDeleteEmployee(null)}
          >
            <p className={`text-${"white"} hover:text-white`}>Cancel</p>
          </button>
          <button
            disabled={isPending}
            className={`bg-bloodRed w-full rounded text-center py-3 px-6 cursor-pointer transition text-lightDark duration-300 ease-in-out hover:text-white hover:bg-opacity-80`}
            onClick={confirmDelete}
          >
            <p className={`text-${"white"} hover:text-white`}>
              {isPending ? "loading..." : "Delete"}
            </p>
          </button>
        </div>
      </TransitionsModal>
      {/* Edit */}
      <TransitionsModal
        open={!!edit}
        setOpen={() => setEdit}
        title={"Edit Employee"}
        handleCloseModal={handleCloseEdit}
      >
        <EditEmployee form={form} setImage={setImage} />

        <div className="flex-row gap-4 flex justify-between mx-auto mt-8">
          <button
            disabled={isEditing}
            className={`bg-customGreen w-full rounded text-center py-3 px-6 cursor-pointer transition text-white duration-300 ease-in-out   hover:bg-opacity-80`}
            onClick={() => {
              form.handleSubmit(onSubmit)();
            }}
          >
            {isEditing ? "Loading..." : "Submit"}
          </button>
        </div>
      </TransitionsModal>
      {/* Confirm Activate/Deactivate */}
      <TransitionsModal
        open={!!activate}
        setOpen={() => setActivate}
        title={
          activate?.status === "active"
            ? "Deactivate Employee?"
            : "Activate Employee?"
        }
        handleCloseModal={handleCloseActive}
      >
        <div className="flex flex-col items-center w-full">
          <p className="text-center text-lightDark">
            Are you sure you want to{" "}
            {activate?.status === "active" ? "Deactivate" : "Activate"} this
            employee?
          </p>
        </div>
        <div className="flex-row gap-4 flex justify-between mx-auto mt-8">
          <button
            className={`bg-yellow-500 w-full rounded text-center py-3 px-6 cursor-pointer transition text-lightDark duration-300 ease-in-out hover:text-white hover:bg-opacity-80`}
            onClick={() => setActivate(null)}
          >
            <p className={`text-${"white"} hover:text-white`}>Cancel</p>
          </button>
          <button
            className={
              activate?.status !== "active"
                ? `bg-customGreen w-full rounded text-center py-3 px-6 cursor-pointer transition text-lightDark duration-300 ease-in-out hover:text-white hover:bg-opacity-80`
                : `bg-bloodRed w-full rounded text-center py-3 px-6 cursor-pointer transition text-lightDark duration-300 ease-in-out hover:text-white hover:bg-opacity-80`
            }
            onClick={confirmActivation}
            disabled={isEditing}
          >
            <p className={`text-${"white"} hover:text-white`}>
              {isEditing
                ? "Loading..."
                : activate?.status === "active"
                ? "Deactivate"
                : "Activate"}
            </p>
          </button>
        </div>
      </TransitionsModal>
      {/* {showModal && (
        <Modal
          showModal={showModal}
          setShowModal={setShowModal}
          closeModal={closeModal}
        />
      )} */}

      {/* Add Employee */}

      <TransitionsModal
        open={showModal}
        setOpen={setShowModal}
        title={"Add Employee"}
        handleCloseModal={closeModal}
      >
        <EditEmployee form={form} setImage={setImage} />

        <div className="flex-row gap-4 flex justify-between mx-auto mt-8">
          <button
            disabled={isRegistering}
            className={`bg-customGreen w-full rounded text-center py-3 px-6 cursor-pointer transition text-white duration-300 ease-in-out   hover:bg-opacity-80`}
            onClick={() => {
              form.handleSubmit(onSubmit)();
            }}
          >
            {isRegistering ? "Loading..." : "Submit"}
          </button>
        </div>
      </TransitionsModal>
      <Screen>
        <ProfileHeaderSection />
        <div className=" border border-[#C0C0C0] w-full lg:mt-2 mt-14 mb-2" />
        <div className="mt-4 flex flex-row justify-between items-center">
          <p className={`text-lightDark `}>Employees</p>
          {dept === "People Operations" && (
            <div className="flex flex-row justify-between gap-2 mx-4">
              <Button
                text="New Employee"
                icon={<BsPlusSquareDotted size={22} color="white" />}
                onClick={handleAdd}
                bg="bloodRed"
              />
            </div>
          )}
        </div>
        <div className=" border border-[#C0C0C0] w-full mt-2 mb-2" />

        <div className="rounded-md border border-[#C0C0C0] py-2 flex flex-row justify-between px-2 mb-4">
          <div className="mb-2 mt-2 flex items-center py-2 rounded-md border-[#C0C0C0] w-[300px] px-2">
            <TableSearch
              filter={filters}
              setFilter={setFilters}
              placeholder={"Employee Name / Department"}
            />
          </div>

          <div className="flex flex-row items-center justify-center">
            <div className="px-2 cursor-pointer" onClick={handleBox}>
              <span>
                <img src={box} alt="" />
              </span>
            </div>
            <div className="px-2 cursor-pointer" onClick={handlelist}>
              <span>
                <img src={list} alt="" />
              </span>
            </div>
          </div>
        </div>

        <div>
          {showList && (
            <DataTable
              columns={column}
              data={transformedEmployees}
              loading={isLoading}
              filters={filters}
              setFilters={setFilters}
            />
          )}

          {showBox && (
            <div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 mx-auto gap-8 overflow-auto">
                {paginatedEmployees.map(
                  (ele: {
                    image: any;
                    id: string;
                    department: string;
                    job_role: string;
                    firstname: string;
                    lastname: string;
                    datejoined: any;
                    email: string;
                    phone: string | number;
                    city: string;
                    country: string;
                    lead: string;
                    status: string;
                    employment_type: string;
                  }) => (
                    <ContactCard
                      handleDelete={handleDelete}
                      handleActive={handleActive}
                      handleClick={handleEdit}
                      image={ele?.image}
                      id={ele.id}
                      department={ele.department}
                      key={ele.id}
                      position={ele.job_role}
                      firstname={ele.firstname}
                      lastname={ele.lastname}
                      date={ele.datejoined}
                      email={ele.email}
                      phone={ele.phone}
                      city={ele.city}
                      country={ele.country}
                      status={ele.status}
                      lead={ele.lead}
                      employment_type={ele.employment_type}
                    />
                  )
                )}
              </div>

              <div className="flex justify-center mt-4 px-2">
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 0))
                  }
                  disabled={currentPage === 0}
                  className={`border-gray-400 border mx-1 px-3 py-2 rounded font-bold ${
                    currentPage === 0 ? "bg-gray-300" : " text-black"
                  }`}
                >
                  Previous
                </button>
                {Array.from(
                  { length: Math.ceil(employee?.length / itemsPerPage) },
                  (_, index) => {
                    const pageNumber = index + 1;

                    return (
                      <button
                        key={index}
                        onClick={() => setCurrentPage(index)}
                        className={`mx-1 px-3 py-2 rounded ${
                          currentPage === index
                            ? "bg-red-200 text-black"
                            : "bg-gray-200"
                        }`}
                      >
                        {pageNumber}
                      </button>
                    );
                  }
                )}
                <button
                  onClick={() =>
                    setCurrentPage((prev) =>
                      Math.min(
                        prev + 1,
                        Math.ceil(employee?.length / itemsPerPage) - 1
                      )
                    )
                  }
                  disabled={
                    currentPage >=
                    Math.ceil(employee?.length / itemsPerPage) - 1
                  }
                  className={`mx-1 border-gray-400 border px-3 py-2 rounded font-bold ${
                    currentPage >=
                    Math.ceil(employee?.length / itemsPerPage) - 1
                      ? "bg-gray-300"
                      : "text-black"
                  }`}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </Screen>
    </div>
  );
};

export default Directory;
